import React, { useState, useEffect, useRef } from "react";
import AppBar from "@mui/material/AppBar";

import SearchBox from "../../common/components/SearchBox";
import {
  Typography,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Divider,
  TextField,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { connect } from "react-redux";
import { Toolbar } from "@mui/material";
import {
  getArchivedDistricts,
  getArchivedPeriods,
  getArchivedPage,
  resetArchived,
  deletePlacementReport,
  downloadAllPlacementReports,
} from "../../../../store/placement/actions";
import ReactPaginate from "react-paginate";
import ConfirmationDialog from "../../common/components/ConfirmationDialog";
import PlacementReportDetailsDialog from "../components/PlacementReportDetailsDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { TreeItem, TreeView } from "@mui/lab";
import { useRequiredAuth } from "../../common/guard/requiredAuth";

const PAGE_SIZE = 15;

// Parse a period tree-node id ("<districtId>-<YYYY-MM>") back into its parts.
// districtId has no "-", periodId is "YYYY-MM", so split on the first "-".
const parsePeriodNodeId = (nodeId) => {
  const i = nodeId.indexOf("-");
  return { districtId: nodeId.slice(0, i), periodId: nodeId.slice(i + 1) };
};

// Group the flat [{ periodId, periodName, teachingLevelId, cnt }] rows for a
// district into one entry per period with a total and per-teaching-level counts.
const groupPeriods = (rows = []) => {
  const periods = [];
  rows.forEach((r) => {
    let p = periods.find((x) => x.periodId === r.periodId);
    if (!p) {
      p = { periodId: r.periodId, periodName: r.periodName, total: 0, byLevel: {} };
      periods.push(p);
    }
    const n = Number(r.cnt) || 0;
    p.total += n;
    p.byLevel[r.teachingLevelId] = (p.byLevel[r.teachingLevelId] || 0) + n;
  });
  return periods;
};

const ArchivedPlacementReports = (props) => {
  useEffect(() => {
    document.title = "TMIS | Placement - Archived Reports";
  }, []);

  const { hasPermissions } = useRequiredAuth();

  const {
    user,
    loading,
    archivedDistricts,
    archivedPeriods,
    archivedPages,
    getArchivedDistricts,
    getArchivedPeriods,
    getArchivedPage,
    deletePlacementReport,
    downloadAllPlacementReports,
  } = props;

  const [expanded, setExpanded] = useState([]);

  // Reload the district list whenever the selected entity / academic year change.
  useEffect(() => {
    setExpanded([]);
    getArchivedDistricts();
  }, [user?.selectedEntity, user?.selectedAcademicYear]);

  // Lazily load a district's periods (once) when it is first expanded.
  const ensurePeriods = (districtId) => {
    if (!archivedPeriods[districtId]) getArchivedPeriods(districtId);
  };

  // Lazily load the first page of a period (once) when it is first expanded.
  const ensureFirstPage = (districtId, periodId) => {
    const key = `${districtId}-${periodId}`;
    if (!archivedPages[key])
      getArchivedPage(districtId, periodId, { page: 1, pageSize: PAGE_SIZE });
  };

  // Auto-expand the first district (to mirror the previous default view).
  useEffect(() => {
    if (archivedDistricts.length && !expanded.length) {
      const first = `${archivedDistricts[0].districtId}`;
      setExpanded([first]);
      ensurePeriods(archivedDistricts[0].districtId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedDistricts]);

  const handleToggleNode = (event, nodeIds) => {
    // Detect nodes that are newly expanded and lazy-load their data.
    const added = nodeIds.filter((id) => !expanded.includes(id));
    added.forEach((id) => {
      if (id.includes("-")) {
        const { districtId, periodId } = parsePeriodNodeId(id);
        ensureFirstPage(districtId, periodId);
      } else {
        ensurePeriods(id);
      }
    });
    setExpanded(nodeIds);
  };

  const [onGeneratePlacementReport, setOnGeneratePlacementReport] =
    useState(false);
  const [teachingLevel, setTeachingLevel] = useState(null);
  const [periodId, setPeriodId] = useState("");
  const [district, setDistrict] = useState({});

  return (
    <>
      <div className="px-4 pt-2">
        <div className="border rounded">
          <AppBar position="static" elevation={0} className="app-bar w-100">
            <Toolbar
              style={{ minHeight: "0px", borderBottom: "3px solid #e9ecef" }}
              className=" my-0 py-1 bg-light"
            >
              <Typography
                variant="p"
                noWrap
                component="div"
                className="d-flex mr-2  align-items-center text-uppercase text-primary font-weight-bold"
              >
                <span className="text-dark ">
                  {" "}
                  ({archivedDistricts.length}) Districts{" "}
                </span>
                <span className="material-icons" style={{ color: "#cecece" }}>
                  chevron_right
                </span>
                <span
                  className="d-block text-truncate"
                  style={{ maxWidth: "70%" }}
                >
                  {user?.selectedEntity?.name}
                </span>
              </Typography>
            </Toolbar>
          </AppBar>

          <div
            style={{
              backgroundColor: "#f3f4f6",
              borderRadius: "0.5rem",
              boxShadow: "0 5px 5px 0 rgb(42 119 144 / 5%)",
              MozBoxShadow: "0 5px 5px 0 rgba(42,119,144,.05)",
              height: "69vh",
              overflowY: "auto",
            }}
            className="px-3"
          >
            <TreeView
              aria-label="controlled"
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              expanded={expanded}
              onNodeToggle={handleToggleNode}
            >
              {archivedDistricts.map((d, index1) => {
                const periodsLoaded = archivedPeriods[d.districtId];
                const periods = groupPeriods(periodsLoaded);
                return (
                  <TreeItem
                    key={`${d.districtId}`}
                    className={`nav-item bg-white mb-2 `}
                    nodeId={`${d.districtId}`}
                    label={
                      <span
                        className="d-flex justify-content-between  align-items-center py-2"
                        style={{ maxWidth: "100%" }}
                      >
                        <span className={`d-block`}>
                          <span
                            className="mr-2 menu-header text-uppercase text-primary"
                            style={{ cursor: "default" }}
                          >
                            {index1 + 1}: {d.districtName} DISTRICT
                          </span>
                        </span>
                      </span>
                    }
                  >
                    {!periodsLoaded && (
                      <TreeItem
                        nodeId={`${d.districtId}-loading`}
                        label={
                          <span className="font-weight-light text-muted py-2 d-block">
                            Loading periods…
                          </span>
                        }
                      />
                    )}

                    {periodsLoaded && !periods.length && (
                      <TreeItem
                        nodeId={`${d.districtId}-empty`}
                        label={
                          <span className="font-weight-light text-muted py-2 d-block">
                            No archived reports
                          </span>
                        }
                      />
                    )}

                    {periods.map((period, index2) => (
                      <TreeItem
                        key={`${d.districtId}-${period.periodId}`}
                        className={`nav-item`}
                        nodeId={`${d.districtId}-${period.periodId}`}
                        label={
                          <span
                            className="d-flex justify-content-between  align-items-center"
                            style={{ maxWidth: "100%" }}
                          >
                            <span
                              className={`d-block font-weight-light text-uppercase`}
                            >
                              <span className="mr-2" style={{ cursor: "default" }}>
                                {index1 + 1}.{index2 + 1}: {period.periodName}{" "}
                                <small>({period.total})</small>
                              </span>
                            </span>

                            <span>
                              {user?.selectedScope?.id !== "RTB" && (
                                <span className="badge badge-pill text-uppercase font-weight-light text-primary mr-1">
                                  {period.byLevel[1] || 0} Pre-primary
                                </span>
                              )}
                              {user?.selectedScope?.id !== "RTB" && (
                                <span className="badge badge-pill text-uppercase font-weight-light text-primary mr-1">
                                  {period.byLevel[2] || 0} Primary
                                </span>
                              )}
                              {user?.selectedScope?.id !== "RTB" && (
                                <span className="badge badge-pill text-uppercase font-weight-light text-primary mr-1">
                                  {period.byLevel[3] || 0} Secondary
                                </span>
                              )}
                              {user?.selectedScope?.id === "RTB" && (
                                <span className="badge badge-pill text-uppercase font-weight-light text-primary mr-1">
                                  {period.byLevel[4] || 0} TVET
                                </span>
                              )}
                              {(user?.selectedEntity?.type === "DISTRICT" ||
                                user?.selectedEntity?.id === "00000000") &&
                                hasPermissions([
                                  "PLACEMENT_REPORTS_NEW_REPORTS_DOWNLOAD",
                                ]) && (
                                  <Tooltip title="Download">
                                    <span className="p-0 text-center btn">
                                      <IconButton
                                        disabled={loading}
                                        className="ml-2 menu-header-icon"
                                        aria-label="settings"
                                        color="primary"
                                        onClick={(e) => {
                                          setDistrict({
                                            id: d.districtId,
                                            name: d.districtName,
                                          });
                                          setPeriodId(period.periodId);
                                          setOnGeneratePlacementReport(true);
                                          e.stopPropagation();
                                        }}
                                      >
                                        <Badge color="info">
                                          <span
                                            className="material-icons"
                                            style={{ fontSize: "18px" }}
                                          >
                                            download
                                          </span>
                                        </Badge>
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                            </span>
                          </span>
                        }
                      >
                        <PlacementList
                          districtId={d.districtId}
                          periodId={period.periodId}
                          pageData={archivedPages[`${d.districtId}-${period.periodId}`]}
                          loadPage={(opts) =>
                            getArchivedPage(d.districtId, period.periodId, {
                              pageSize: PAGE_SIZE,
                              ...opts,
                            })
                          }
                          deletePlacementReport={deletePlacementReport}
                          user={user}
                          hasPermissions={hasPermissions}
                        />

                        <div className="ml-n3">
                          <Divider />
                          <Divider />
                        </div>
                      </TreeItem>
                    ))}
                  </TreeItem>
                );
              })}
            </TreeView>

            {!archivedDistricts.length && (
              <div className="jumbotron jumbotron-fluid text-center">
                <div className="container py-5 ">
                  <p className="lead">
                    {loading ? "Fetching data..." : "No record found"}{" "}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {onGeneratePlacementReport && (
        <ConfirmationDialog
          disabled={!teachingLevel}
          confirmationDialog={onGeneratePlacementReport}
          message={
            <>
              <p className="mb-0 mt-3 text-uppercase text-primary">
                Download Placement Report
              </p>

              <div className="mt-3">
                <Autocomplete
                  fullWidth
                  size="small"
                  defaultValue={null}
                  value={teachingLevel || null}
                  options={
                    user.selectedScope.id === "RTB"
                      ? [{ id: 4, name: "TVET" }]
                      : user.selectedEntity.role === "DEO_1"
                      ? [
                          { id: 1, name: "PRE_PRIMARY" },
                          { id: 2, name: "PRIMARY" },
                        ]
                      : user.selectedEntity.role === "DEO_2"
                      ? [{ id: 3, name: "SECONDARY" }]
                      : [
                          { id: 1, name: "PRE_PRIMARY" },
                          { id: 2, name: "PRIMARY" },
                          { id: 3, name: "SECONDARY" },
                        ]
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(event, teachingLevel) => {
                    setTeachingLevel(teachingLevel || null);
                  }}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, teachingLevel) => (
                    <Box component="li" {...props}>
                      {teachingLevel.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Teaching level"
                      name="teachingLevel"
                      placeholder="Teaching level"
                      inputProps={{ ...params.inputProps }}
                    />
                  )}
                />
              </div>
            </>
          }
          btnMessage={"Download"}
          setConfirmationDialog={setOnGeneratePlacementReport}
          onYes={() => {
            downloadAllPlacementReports(
              {
                districtId: district?.id,
                districtName: district?.name,
                teachingLevelId: teachingLevel?.id,
                periodId: periodId,
              },
              setOnGeneratePlacementReport
            );
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  user,
  loading,
  archivedDistricts,
  archivedPeriods,
  archivedPages,
}) => {
  return { user, loading, archivedDistricts, archivedPeriods, archivedPages };
};
export default connect(mapStateToProps, {
  getArchivedDistricts,
  getArchivedPeriods,
  getArchivedPage,
  resetArchived,
  deletePlacementReport,
  downloadAllPlacementReports,
})(ArchivedPlacementReports);

// A single period's school rows — server-side paginated + searched.
const PlacementList = (props) => {
  const {
    districtId,
    periodId,
    pageData,
    loadPage,
    deletePlacementReport,
    user,
    hasPermissions,
  } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const firstSearchRun = useRef(true);

  const rows = pageData?.rows || [];
  const total = pageData?.total || 0;
  const pageSize = pageData?.pageSize || PAGE_SIZE;
  const currentPage = pageData?.page || 1;
  const pageCount = Math.ceil(total / pageSize);

  // Debounced server-side search (reset to page 1). Skip the initial mount run
  // so we don't duplicate the first page load triggered on expand.
  useEffect(() => {
    if (firstSearchRun.current) {
      firstSearchRun.current = false;
      return;
    }
    const t = setTimeout(() => {
      loadPage({ page: 1, search: searchTerm });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const onSearch = (term) => setSearchTerm(term);

  const handlePageClick = (event) => {
    loadPage({ page: event.selected + 1, search: searchTerm });
  };

  const startIndex = (currentPage - 1) * pageSize;

  return (
    <>
      {!pageData && (
        <p className="font-weight-light text-muted py-2 pl-2 mb-0">Loading…</p>
      )}

      {!!pageData && (
        <table className="table table-striped table-hover table-sm">
          <thead>
            <tr>
              <th
                scope="col"
                rowSpan={2}
                style={{
                  borderLeft: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
              >
                #
              </th>
              <th
                scope="col"
                rowSpan={2}
                style={{
                  borderLeft: "solid 1px #c6c7ce",
                  backgroundColor: "#dbeafe",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
              >
                SCHOOLS
              </th>
              <th
                rowSpan={2}
                className="text-left text-uppercase"
                style={{
                  backgroundColor: "#d1def1",
                  borderLeft: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
              >
                TEACHING LEVEL
              </th>
              <th
                colSpan={3}
                style={{
                  backgroundColor: "#dbeafe",
                  borderLeft: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
                className="text-center text-uppercase"
              >
                Budget Posts
              </th>
              <th
                colSpan={3}
                style={{
                  backgroundColor: "#d1fae5",
                  borderLeft: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
                className="text-center text-uppercase"
              >
                Active Teachers
              </th>
              <th
                colSpan={3}
                style={{
                  backgroundColor: "#fef3c7",
                  borderLeft: "solid 1px #c6c7ce",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                  borderTop: "1px solid #a8b6c3",
                }}
                className="text-center text-uppercase"
              >
                Vacant Posts
              </th>
              <th colSpan={2}>
                <SearchBox onSearch={onSearch} placeholder="Search…" />
              </th>
            </tr>
            <tr style={{ backgroundColor: "#f0f2f5" }}>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#dbeafe",
                  borderLeft: "solid 1px #c6c7ce",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A2
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#dbeafe",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A1
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#dbeafe",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A0
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#d1fae5",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A2
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#d1fae5",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A1
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#d1fae5",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A0
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#fef3c7",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A2
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  backgroundColor: "#fef3c7",
                  borderRight: "solid 1px #c6c7ce",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A1
              </th>
              <th
                className="text-center"
                scope="col"
                style={{
                  borderRight: "solid 1px #c6c7ce",
                  backgroundColor: "#fef3c7",
                  borderBottom: "2px solid #a8b6c3",
                }}
              >
                A0
              </th>
              <th scope="col" className="text-center">
                Status
              </th>
              <th style={{ width: "64px" }} className="text-right">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((placementReport, i) => (
              <ItemCard
                key={placementReport.id}
                user={user}
                index={startIndex + i}
                placementReport={placementReport}
                deletePlacementReport={deletePlacementReport}
                hasPermissions={hasPermissions}
              />
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={14} className="text-center text-muted py-3">
                  No record found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {pageCount > 1 && (
        <div className="mb-3 mt-n2">
          <ReactPaginate
            breakLabel="..."
            nextLabel={
              <>
                <span className="pr-1 d-none d-md-inline">Next</span>
                <i className="fas fa-angle-double-right"></i>
              </>
            }
            previousLabel={
              <>
                <i className="fas fa-angle-double-left"></i>
                <span className="pl-1  d-none d-md-inline">Previous</span>
              </>
            }
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            onPageChange={handlePageClick}
            pageCount={pageCount}
            forcePage={currentPage - 1}
            renderOnZeroPageCount={null}
            containerClassName="pagination"
            pageLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
            previousLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
            nextLinkClassName="btn btn-outline-dark btn-sm ml-1"
            activeLinkClassName="active"
          />
        </div>
      )}
    </>
  );
};

const ItemCard = (props) => {
  const { placementReport, index, deletePlacementReport, user, hasPermissions } =
    props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const [confirmCancelReport, setConfirmCancelReport] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  return (
    <>
      <tr className={``}>
        <th
          scope="row"
          className="text-left"
          style={{ borderLeft: "solid 1px #c6c7ce" }}
        >
          {index + 1}
        </th>

        <td
          className="text-uppercase"
          style={{ borderLeft: "solid 1px #c6c7ce" }}
        >
          {placementReport.schoolName} / {placementReport.sectorName}
        </td>
        <td
          className="text-left"
          style={{
            borderLeft: "solid 1px #c6c7ce",
            borderRight: "solid 1px #c6c7ce",
          }}
        >
          {placementReport.teachingLevelName}
        </td>

        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalBudgetPostsA2 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalBudgetPostsA1 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalBudgetPostsA0 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalTeachersA2 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalTeachersA1 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.totalTeachersA0 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.vacantBudgetPostsA2 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.vacantBudgetPostsA1 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          {+placementReport.vacantBudgetPostsA0 || "-"}
        </td>
        <td className="text-center" style={{ borderRight: "solid 1px #c6c7ce" }}>
          <span
            className={`p-2 badge badge-${
              placementReport.statusId === 1
                ? "light"
                : placementReport.statusId === 2
                ? "secondary"
                : placementReport.statusId === 4
                ? "info"
                : placementReport.statusId === 6
                ? "success"
                : "danger"
            }`}
          >
            {placementReport.statusName}
          </span>
        </td>
        <td className="text-right" style={{ borderRight: "solid 1px #c6c7ce" }}>
          <IconButton
            size="small"
            className="ml-2"
            onClick={handleOpenMenu}
            aria-label="settings"
          >
            <span className="material-icons">more_vert</span>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={(e) => {
              handleCloseMenu();
              e.stopPropagation();
            }}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 2,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 15,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <span>
              <MenuItem
                onClick={(e) => {
                  handleCloseMenu();
                  setShowPreviewDialog(true);
                }}
                className="text-primary font-weight-bolder"
              >
                <span className="material-icons  mr-1">preview</span>
                Preview
              </MenuItem>
            </span>
          </Menu>
        </td>
      </tr>

      {showPreviewDialog && (
        <PlacementReportDetailsDialog
          showDialog={showPreviewDialog}
          setShowDialog={setShowPreviewDialog}
          placementReport={placementReport}
          isEditable={
            placementReport.statusId === 1 &&
            user.selectedEntity.role === "HEAD_TEACHER"
          }
        />
      )}

      {confirmCancelReport && (
        <ConfirmationDialog
          confirmationDialog={confirmCancelReport}
          message={
            <>
              <p className="mb-0 mt-3">
                Are you sure you want to{" "}
                <strong className="text-danger">cancel</strong> this placement
                report?
              </p>
            </>
          }
          setConfirmationDialog={setConfirmCancelReport}
          onYes={() => {
            deletePlacementReport(placementReport.id, setConfirmCancelReport);
          }}
        />
      )}
    </>
  );
};
